import { OptionsFill24Icon } from "@vygruppen/spor-icon-react";
import { FC, ReactNode, useState } from "react";
import {
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  VStack,
} from "shared/components/Spor";
import styled from "styled-components";

/**
 * Styling that turns the buttons rendered inside the overflow popover into
 * left-aligned menu items (shared by the alternative transport panels).
 */
export const KebabMenuContent = styled(PopoverContent)`
  && {
    background-color: ${({ theme }) => theme.colorBackgroundTertiary};
    border: 1px solid ${({ theme }) => theme.colorOutlineCore};
    box-shadow: none;
    border-radius: 12px;
    padding: 6px;
  }
  && && {
    border: none;
    box-shadow: none;
    background: transparent;
    padding: 0;
    min-width: 0;
    width: 100%;
    align-self: stretch;
  }
  && && > * {
    width: 100%;
  }

  &.spor-popover__body {
    width: 100%;
  }

  [data-part="arrow"] {
    display: none;
  }

  button {
    display: flex;
    align-items: center;
    width: 100% !important;
    max-width: 100% !important;
    justify-content: flex-start;
    white-space: nowrap;
    font-weight: 400 !important;
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
    border-radius: 9px;
    color: ${({ theme }) => theme.colorTextMain} !important;
  }

  button > * {
    white-space: nowrap;
    font-weight: 400 !important;
  }

  button:focus,
  button:focus-visible {
    box-shadow: none !important;
    outline: none !important;
  }

  button svg {
    color: ${({ theme }) => theme.colorTextMain};
  }

  button:hover {
    background: ${({ theme }) => theme.colorTextMain}14 !important;
  }

  button::after {
    content: "→";
    margin-left: auto;
    padding-left: 12px;
    font-size: 18px;
    line-height: 1;
  }
`;

/**
 * Only visible on small panel widths — collapses actions into the kebab menu.
 */
export const KebabOnly = styled.div`
  @container altTransportPage (width >= 1696px) {
    display: none;
  }
`;

/**
 * Only visible on wide panel widths — shows actions inline.
 */
export const InlineActions = styled.div`
  display: none;

  @container altTransportPage (width >= 1696px) {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

type ActionsKebabMenuProps = {
  children: ReactNode;
};

/**
 * Triple-dot overflow menu shown on small panel widths. The action buttons
 * passed as children are rendered as menu items and the menu closes on select.
 */
export const ActionsKebabMenu: FC<ActionsKebabMenuProps> = ({ children }) => {
  const [isKebabOpen, setIsKebabOpen] = useState(false);

  return (
    <KebabOnly>
      <Popover
        open={isKebabOpen}
        onOpenChange={(e) => setIsKebabOpen(e.open)}
        positioning={{ placement: "bottom-end" }}
      >
        <PopoverTrigger>
          <IconButton
            variant="tertiary"
            size="sm"
            aria-label="Flere valg"
            icon={<OptionsFill24Icon />}
          />
        </PopoverTrigger>
        <KebabMenuContent>
          <VStack
            align="stretch"
            gap="1"
            w="100%"
            onClick={() => setIsKebabOpen(false)}
          >
            {children}
          </VStack>
        </KebabMenuContent>
      </Popover>
    </KebabOnly>
  );
};
